import { ethers } from 'ethers';
import {
  CONTRACTS, CHAIN_CONFIG, ERC721_ABI, HOOTLAND_AGENT_ABI, OWL_SDT_ABI,
} from '../contracts';

// ─── Providers ────────────────────────────────────────────────────────

export const getReadProvider = () =>
  new ethers.JsonRpcProvider(CHAIN_CONFIG.rpcUrl);

export const getContracts = (signerOrProvider) => {
  // Return null contracts if addresses not set (not deployed yet)
  if (!CONTRACTS.HootlandAgent || CONTRACTS.HootlandAgent === '') {
    return {
      hootlandAgent: null,
      owlSDT: null,
    };
  }
  
  return {
    hootlandAgent: new ethers.Contract(CONTRACTS.HootlandAgent, HOOTLAND_AGENT_ABI, signerOrProvider),
    owlSDT:       new ethers.Contract(CONTRACTS.owlSDT || '0x0000000000000000000000000000000000000000', OWL_SDT_ABI, signerOrProvider),
  };
};

// ─── Network ──────────────────────────────────────────────────────────

export const enforceBaseNetwork = async () => {
  if (!window.ethereum) throw new Error('No wallet found');
  const chainId = await window.ethereum.request({ method: 'eth_chainId' });
  console.log('🦉 Current chainId:', chainId);
  console.log('🦉 Expected chainId:', CHAIN_CONFIG.chainId);
  if (chainId.toLowerCase() === CHAIN_CONFIG.chainId.toLowerCase()) return;

  try {
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: CHAIN_CONFIG.chainId }],
    });
  } catch (err) {
    if (err.code === 4902) {
      await window.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: CHAIN_CONFIG.chainId,
          chainName: CHAIN_CONFIG.name,
          nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
          rpcUrls: [CHAIN_CONFIG.rpcUrl],
          blockExplorerUrls: [CHAIN_CONFIG.blockExplorer],
        }],
      });
    } else {
      throw err;
    }
  }
};

// ─── NFT Discovery ────────────────────────────────────────────────────

// Sample/demo NFTs for display before contracts are deployed
const SAMPLE_NFTS = [
  {
    collection: '0xBaseBros',
    collectionName: 'Base Bros',
    tokenId: '1',
    name: 'Base Bro #1',
    image: 'https://api.basebros.xyz/images/1.png',
    deposited: false,
  },
];

// Discover NFTs by scanning events (works without tokenOfOwnerByIndex)
export const discoverAllNFTs = async (walletAddress) => {
  if (!CONTRACTS.HootlandAgent || CONTRACTS.HootlandAgent === '') {
    console.log('🦉 Contracts not deployed yet');
    return [];
  }
  
  try {
    const provider = getReadProvider();
    const agentContract = new ethers.Contract(CONTRACTS.HootlandAgent, HOOTLAND_AGENT_ABI, provider);
    
    // Get Transfer events to find tokens owned by wallet
    const filter = agentContract.filters.Transfer(null, walletAddress, null);
    const events = await agentContract.queryFilter(filter, -10000); // Last 10k blocks
    
    const tokenIds = [...new Set(events.map(e => e.args.tokenId.toString()))];
    const nfts = [];
    
    for (const tokenId of tokenIds) {
      try {
        // Verify still owned
        const owner = await agentContract.ownerOf(tokenId);
        if (owner.toLowerCase() !== walletAddress.toLowerCase()) continue;
        
        const agentInfo = await agentContract.getAgentInfo(tokenId);
        
        nfts.push({
          collection: CONTRACTS.HootlandAgent,
          collectionName: 'Hootland Agent',
          tokenId: tokenId,
          name: `Agent #${tokenId}`,
          image: null,
          agentId: tokenId,
          tier: agentInfo.tier,
          multiplier: agentInfo.multiplier.toString(),
          deposited: agentInfo.agentType === 1,
        });
      } catch (e) {
        console.warn('Error fetching agent:', e);
      }
    }
    
    return nfts;
  } catch (error) {
    console.warn('NFT discovery error:', error);
    return [];
  }
};

// ─── User Agents ──────────────────────────────────────────────────────

export const fetchUserAgents = async (walletAddress) => {
  if (!CONTRACTS.HootlandAgent || CONTRACTS.HootlandAgent === '') {
    console.log('🦉 Contracts not deployed yet');
    return [];
  }
  
  try {
    const provider = getReadProvider();
    const agentContract = new ethers.Contract(CONTRACTS.HootlandAgent, HOOTLAND_AGENT_ABI, provider);
    
    const agents = [];
    
    // Get total supply and scan all agents
    const supply = await agentContract.genesisSupply();
    const total = Number(supply);
    
    console.log(`🦉 Scanning ${total} agents for wallet ${walletAddress.slice(0,6)}...`);
    
    // Scan all agent IDs from 1 to total
    for (let i = 1; i <= total; i++) {
      try {
        const owner = await agentContract.ownerOf(i);
        if (owner.toLowerCase() !== walletAddress.toLowerCase()) continue;
        
        const agentInfo = await agentContract.getAgentInfo(i);
        const earnings = await agentContract.calculateEarnings(i);
        
        agents.push({
          agentId: i.toString(),
          tier: agentInfo.tier,
          multiplier: agentInfo.multiplier.toString(),
          x: agentInfo.x.toString(),
          y: agentInfo.y.toString(),
          lastClaimTime: agentInfo.lastClaimTime.toString(),
          totalEarned: agentInfo.totalEarned.toString(),
          pendingEarnings: earnings.toString(),
          agentType: agentInfo.agentType === 0 ? 'Genesis' : 'Wrapped',
          active: agentInfo.active,
        });
      } catch (e) {
        // Skip if token doesn't exist or other error
      }
    }
    
    console.log(`🦉 Found ${agents.length} agents`);
    return agents;
  } catch (error) {
    console.warn('Fetch agents error:', error);
    return [];
  }
};

// ─── Token Balance ────────────────────────────────────────────────────

export const fetchOwlBalance = async (walletAddress) => {
  if (!CONTRACTS.owlSDT || CONTRACTS.owlSDT === '') {
    return '0';
  }
  
  try {
    const provider = getReadProvider();
    const owlContract = new ethers.Contract(CONTRACTS.owlSDT, OWL_SDT_ABI, provider);
    const balance = await owlContract.balanceOf(walletAddress);
    
    // Try to add token to MetaMask
    try {
      if (window.ethereum && window.ethereum.request) {
        await window.ethereum.request({
          method: 'wallet_watchAsset',
          params: {
            type: 'ERC20',
            options: {
              address: CONTRACTS.owlSDT,
              symbol: 'owlSDT',
              decimals: 18,
              image: 'https://gateway.pinata.cloud/ipfs/QmYourLogoImageCID/owl-logo.png',
            },
          },
        });
      }
    } catch (addError) {
      console.log('Token already added or user declined:', addError);
    }
    
    return ethers.formatUnits(balance, 18);
  } catch (error) {
    console.warn('OWL balance error:', error);
    return '0';
  }
};

// ─── Tier Info ────────────────────────────────────────────────────────

const TIERS = {
  0: { name: 'Agent', multiplier: 1.2 },
  1: { name: 'Sentinel', multiplier: 1.5 },
  2: { name: 'Overseer', multiplier: 2.0 },
  3: { name: 'Elder', multiplier: 3.0 },
};

export const getTierInfo = (tierId) => TIERS[tierId] || TIERS[0];

// ─── Pricing ──────────────────────────────────────────────────────────

export const getCurrentPrice = async () => {
  if (!CONTRACTS.HootlandAgent || CONTRACTS.HootlandAgent === '') {
    return '0.0001';
  }
  
  try {
    const provider = getReadProvider();
    const agentContract = new ethers.Contract(CONTRACTS.HootlandAgent, HOOTLAND_AGENT_ABI, provider);
    const price = await agentContract.currentPrice();
    return ethers.formatUnits(price, 18);
  } catch (error) {
    return '0.0001';
  }
};

export const getMintStats = async () => {
  if (!CONTRACTS.HootlandAgent || CONTRACTS.HootlandAgent === '') {
    return { minted: 0, max: 25000, price: '0.0001' };
  }
  
  try {
    const provider = getReadProvider();
    const agentContract = new ethers.Contract(CONTRACTS.HootlandAgent, HOOTLAND_AGENT_ABI, provider);
    const [minted, max] = await Promise.all([
      agentContract.genesisSupply(),
      agentContract.MAX_GENESIS(),
    ]);
    const price = await agentContract.currentPrice();
    
    return {
      minted: minted.toString(),
      max: max.toString(),
      price: ethers.formatUnits(price, 18),
    };
  } catch (error) {
    return { minted: 0, max: 25000, price: '0.0001' };
  }
};
